import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MessageCircle, Search, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import ChatDrawer from '../components/ChatDrawer';
import Footer from '../components/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authHeaders = () => {
  const t = localStorage.getItem('khadam_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const MessagesPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [convs, setConvs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [search, setSearch] = useState('');

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/conversations/me`, { headers: authHeaders() });
      setConvs(data);
    } catch (e) {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }
    load();
    const iv = setInterval(load, 8000);
    return () => clearInterval(iv);
    // eslint-disable-next-line
  }, [isAuthenticated]);

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-blue-50 to-white pt-20 px-4">
        <div className="bg-white rounded-2xl shadow-xl p-10 text-center max-w-md">
          <AlertCircle size={48} className="text-orange-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">{t('chat.loginRequired')}</h2>
          <button
            onClick={() => navigate('/')}
            data-testid="messages-home"
            className="mt-4 bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-xl px-6 py-3"
          >
            {t('register.backHome')}
          </button>
        </div>
      </div>
    );
  }

  const filtered = convs.filter((c) =>
    c.other_user_name?.toLowerCase().includes(search.toLowerCase())
  );

  const formatTime = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch {
      return '';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50/30 to-white pt-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <p className="text-sm text-gray-500 mb-1">{t('nav.messages')}</p>
          <h1 className="text-3xl font-bold text-gray-900" data-testid="messages-title">{t('chat.inbox')}</h1>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-4 border-b border-gray-100">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder={t('chat.searchPlaceholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="messages-search"
                className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>
          </div>

          {loading ? (
            <div className="p-10 text-center text-gray-400 text-sm">{t('common.loading')}</div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center">
              <MessageCircle size={40} className="text-gray-300 mx-auto mb-3" />
              <p className="font-medium text-gray-700 mb-1">{t('chat.emptyInbox')}</p>
              <p className="text-sm text-gray-500">{t('chat.emptyInboxHint')}</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100" data-testid="conversations-list">
              {filtered.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setActive(c)}
                  data-testid={`conv-${c.other_user_id}`}
                  className="w-full flex items-start gap-3 p-4 hover:bg-gray-50 transition text-left"
                >
                  {c.other_photo ? (
                    <img src={c.other_photo} alt="" className="w-12 h-12 rounded-full object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <span className="font-bold text-blue-600">{c.other_user_name?.[0]?.toUpperCase()}</span>
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="font-semibold text-gray-900 truncate">{c.other_user_name}</span>
                      <span className="text-xs text-gray-400 flex-shrink-0">{formatTime(c.last_message_at)}</span>
                    </div>
                    <p className="text-sm text-gray-500 truncate">{c.last_message}</p>
                    {c.other_skill && <p className="text-xs text-gray-400 mt-0.5">{c.other_skill}</p>}
                  </div>
                  {c.unread_count > 0 && (
                    <span
                      data-testid={`unread-${c.other_user_id}`}
                      className="flex-shrink-0 bg-blue-500 text-white text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center"
                    >
                      {c.unread_count}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {active && (
        <ChatDrawer
          open={!!active}
          onClose={() => {
            setActive(null);
            load();
          }}
          peerUserId={active.other_user_id}
          peerName={active.other_user_name}
          peerPhoto={active.other_photo}
          peerSkill={active.other_skill}
        />
      )}

      <Footer />
    </div>
  );
};

export default MessagesPage;
